import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { RunDetail, RunEvent } from '@/entities/sender.entity';
import { SENDER_HIDDEN_EVENT_TYPES } from '@/entities/sender-events';
import {
	isExternalChannelRun,
	resolveExternalChannelUrlFromSnapshot,
} from '@/lib/sender-apk-delivery';
import RunEventCard from '@/pages/sender/run-event-card';
import RunPipelineCard from '@/pages/sender/run-pipeline-card';
import SenderStatusChip, { runStatusToChipTone } from '@/pages/sender/components/sender-status-chip';
import { senderInlineFactClass, senderSurfaceClass } from '@/pages/sender/components/sender-surface';
import { cn } from '@/lib/utils';
import { useMemo, useState, type ReactNode } from 'react';

import { POLL_INTERVAL_MS, TERMINAL_STATUSES } from '@/pages/sender/sender-run-detail.constants';
import { SENDER_RUN_STATUS_LABELS } from '@/pages/sender/sender-run-status-labels';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';

export type SenderRunDetailActions = {
	onCancel?: () => void | Promise<void>;
	onResume?: () => void | Promise<void>;
	cancelling?: boolean;
	resuming?: boolean;
};

type SenderRunDetailViewProps = {
	run: RunDetail;
	events: RunEvent[];
	actions?: SenderRunDetailActions;
};

export default function SenderRunDetailView({ run, events, actions }: SenderRunDetailViewProps) {
	const visibleEvents = useMemo(
		() => events.filter(event => !SENDER_HIDDEN_EVENT_TYPES.has(event.eventType)),
		[events],
	);

	const counters = run.counters;
	const audienceResolved = counters?.audienceTotal != null;
	const runSourceLabel =
		run.runSource === 'landing'
			? 'Лендинг'
			: run.runSource === 'device'
				? 'Устройство'
				: run.runSource === 'panel_manual'
					? 'Панель'
					: run.runSource === 'panel_auto_campaign'
						? 'Авторассылка'
						: run.runSource ?? null;

	const [cancelOpen, setCancelOpen] = useState(false);
	const canResume = run.status === 'failed' && run.resumable;
	const canCancel = !TERMINAL_STATUSES.has(run.status);
	const externalChannelUrl = resolveExternalChannelUrlFromSnapshot(run.deliveryProfileSnapshot);
	const showCaption = !isExternalChannelRun(run.deliveryProfileSnapshot) && run.caption.trim().length > 0;

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex flex-wrap items-center gap-2'>
				<SenderStatusChip
					label={SENDER_RUN_STATUS_LABELS[run.status] ?? 'Статус'}
					tone={runStatusToChipTone(run.status)}
				/>
				{runSourceLabel ? (
					<span className='text-[13px] text-muted-foreground'>{runSourceLabel}</span>
				) : null}
			</div>
			<div className={cn(senderSurfaceClass, 'flex flex-col gap-3 p-3 text-sm')}>
					<RunPipelineCard run={run} events={events} />

					{showCaption ? (
						<div className={senderInlineFactClass}>
							<p className='text-[13px] text-muted-foreground mb-1'>Текст сообщения</p>
							<p className='whitespace-pre-wrap break-words text-[15px]'>{run.caption}</p>
						</div>
					) : null}

					{externalChannelUrl ? (
					<div className={senderInlineFactClass}>
						<p className='text-[13px] text-muted-foreground mb-1'>Канал</p>
						<p className='break-all text-[15px]'>{externalChannelUrl}</p>
					</div>
				) : null}

					<div className='grid grid-cols-2 gap-2'>
						<Stat label='Создан' value={new Date(run.createdAt).toLocaleString()} />
						{run.startedAt ? <Stat label='Старт' value={new Date(run.startedAt).toLocaleString()} /> : null}
						{run.finishedAt ? <Stat label='Окончание' value={new Date(run.finishedAt).toLocaleString()} /> : null}
						{run.scheduleDate ? (
							<Stat label='Время отправки' value={new Date(run.scheduleDate).toLocaleString()} />
						) : null}
						{run.sessionLabel ? <Stat label='Метка сессии' value={run.sessionLabel} /> : null}
						{runSourceLabel ? <Stat label='Источник' value={runSourceLabel} /> : null}
						{run.authJobId ? <Stat label='Вход с лендинга' value={run.authJobId} /> : null}
						{run.linkId ? <Stat label='Ссылка' value={run.linkId} /> : null}
						{run.landingHostname ? <Stat label='Домен' value={run.landingHostname} /> : null}
						{run.clientId ? <Stat label='Клиент' value={run.clientId} /> : null}
					</div>

					{counters ? (
						<div className='flex flex-col gap-3'>
							<StatsSection title='Сессия'>
								<Stat label='Контактов' value={formatCounter(counters.audienceTotal, audienceResolved)} />
								<Stat
									label='Получателей'
									value={formatCounter(counters.audienceTarget, audienceResolved)}
								/>
								<Stat label='Не взаимные' value={formatCounter(counters.contactsNonMutual, audienceResolved)} />
								<Stat label='Боты в контактах' value={formatCounter(counters.contactsBots, audienceResolved)} />
								<Stat label='Удалённые' value={formatCounter(counters.contactsDeleted, audienceResolved)} />
								<Stat label='Диалогов всего' value={formatCounter(counters.dialogsTotal, audienceResolved)} />
								<Stat label='Личные' value={formatCounter(counters.dialogsPrivate, audienceResolved)} />
								<Stat label='Группы' value={formatCounter(counters.dialogsGroups, audienceResolved)} />
								<Stat label='Каналы' value={formatCounter(counters.dialogsChannels, audienceResolved)} />
								<Stat label='Боты в диалогах' value={formatCounter(counters.dialogsBots, audienceResolved)} />
							</StatsSection>

							<StatsSection title='Рассылка'>
								<Stat
									label='На отправке'
									value={String(counters.scheduledCount ?? run.scheduledCount ?? 0)}
								/>
								<Stat
									label='Отправлено'
									value={String(counters.deliveredCount ?? run.deliveredCount ?? 0)}
								/>
								<Stat label='Ошибки планирования' value={String(counters.skippedPeers ?? 0)} />
								{(counters.skippedPeers ?? 0) > 0 ? (
									<>
										<Stat label='Бан' value={String(counters.skippedBanned ?? 0)} />
										<Stat label='Нет прав' value={String(counters.skippedNoRights ?? 0)} />
										<Stat label='Flood' value={String(counters.skippedPeerFlood ?? 0)} />
										<Stat label='Прочее' value={String(counters.skippedUnknown ?? 0)} />
									</>
								) : null}
							</StatsSection>
						</div>
					) : null}

					{run.errorMessage ? (
						<div className='rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive text-sm'>
							{run.errorMessage}
						</div>
					) : null}

					{!TERMINAL_STATUSES.has(run.status) ? (
						<p className='text-xs text-muted-foreground'>Обновление каждые {POLL_INTERVAL_MS / 1000}с…</p>
					) : null}

					{canResume ? (
						<div className='rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground'>
							{run.resumeHint ?? 'Можно продолжить с места ошибки'}
						</div>
					) : null}

					{actions && (canResume || canCancel) ? (
						<div className='flex flex-wrap gap-2'>
							{canResume && actions.onResume ? (
								<Button
									type='button'
									size='sm'
									disabled={actions.resuming}
									onClick={() => void actions.onResume?.()}
								>
									{actions.resuming ? <Spinner className='size-4' /> : 'Возобновить'}
								</Button>
							) : null}
							{canCancel && actions.onCancel ? (
								<Button
									type='button'
									variant='destructive'
									className='min-h-11'
									disabled={actions.cancelling}
									onClick={() => setCancelOpen(true)}
								>
									Отменить
								</Button>
							) : null}
						</div>
					) : null}
			</div>
			<Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Отменить запуск?</DialogTitle>
					</DialogHeader>
					<DialogFooter className='flex-col gap-2 sm:flex-col'>
						<Button
							type='button'
							variant='destructive'
							className='min-h-11'
							disabled={actions?.cancelling}
							onClick={() => void actions?.onCancel?.()}
						>
							{actions?.cancelling ? <Spinner className='size-4' /> : 'Отменить'}
						</Button>
						<Button type='button' variant='outline' className='min-h-11' onClick={() => setCancelOpen(false)}>
							Отмена
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<div className='flex flex-col gap-2'>
				<p className='text-[13px] text-muted-foreground'>События · {visibleEvents.length}</p>
				{visibleEvents.length === 0 ? (
					<p className='text-[15px] text-muted-foreground py-4 text-center'>Событий пока нет</p>
				) : (
					<div className={cn(senderSurfaceClass, 'flex flex-col overflow-hidden')}>
						{visibleEvents.map(event => (
							<RunEventCard
								key={event.id}
								event={event}
								isRunActive={!TERMINAL_STATUSES.has(run.status)}
								audienceTarget={run.counters?.audienceTarget}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
}

function formatCounter(value: number | undefined, resolved: boolean): string {
	if (!resolved) return '—';
	return String(value ?? 0);
}

function StatsSection({ title, children }: { title: string; children: ReactNode }) {
	return (
		<div className={cn(senderInlineFactClass)}>
			<p className='text-xs font-medium text-muted-foreground mb-2'>{title}</p>
			<div className='grid grid-cols-2 gap-2'>{children}</div>
		</div>
	);
}

function Stat({ label, value }: { label: string; value: string }) {
	return (
		<div className='flex flex-col gap-0.5'>
			<span className='text-xs text-muted-foreground'>{label}</span>
			<span className={cn('font-medium')}>{value}</span>
		</div>
	);
}
